import { ChevronUpIcon } from '@heroicons/react/16/solid'
import { ArrowRightStartOnRectangleIcon, Cog6ToothIcon } from '@heroicons/react/16/solid'
import {
  Avatar,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownLabel,
  DropdownDivider,
} from '@/components/ui'
import type { User } from '@/types'

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/**
 * Account menu in the sidebar footer: avatar + name/email and a dropdown with
 * Settings and Sign out. `onSignOut` is wired to Devise's destroy session in
 * Phase 2. Labels are passed in (localized by the caller). Renders nothing when
 * there is no signed-in user.
 */
export function UserMenu({
  user,
  labels,
  onSignOut,
}: {
  user: User | null
  labels: { settings: string; signOut: string }
  onSignOut: () => void
}) {
  if (!user) return null

  return (
    <Dropdown>
      <DropdownButton
        as="button"
        className="flex w-full items-center gap-3 rounded-control px-3 py-2 text-left transition-colors hover:bg-white/5"
      >
        <Avatar initials={initials(user.name)} className="size-9 bg-brand-turquoise text-white" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-white">{user.name}</span>
          <span className="block truncate text-xs text-white/60">{user.email}</span>
        </span>
        <ChevronUpIcon aria-hidden="true" className="size-4 shrink-0 text-white/60" />
      </DropdownButton>
      <DropdownMenu className="min-w-60" anchor="top start">
        <DropdownItem href="/settings">
          <Cog6ToothIcon aria-hidden="true" data-slot="icon" />
          <DropdownLabel>{labels.settings}</DropdownLabel>
        </DropdownItem>
        <DropdownDivider />
        <DropdownItem onClick={onSignOut}>
          <ArrowRightStartOnRectangleIcon aria-hidden="true" data-slot="icon" />
          <DropdownLabel>{labels.signOut}</DropdownLabel>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  )
}

export { initials as userInitials }
